import React from "react";
import { Pie } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { IconOverview } from "../ui/icons";
import { useTheme } from "../../context/ThemeContext";
import { useApiData } from "../../hooks/useApiData";
import { unwrapArray } from "../../utils/api";
import { CHART_PALETTE, formatNumber, getChartColors, tooltipStyle } from "./chartTheme";
import { ChartSkeleton } from "../ui/Skeleton";
import { EmptyState, ErrorState } from "../ui/StateBlock";
import Card from "../ui/Card";

ChartJS.register(ArcElement, Tooltip, Legend, ChartDataLabels);

export default function CategoryChart({ startDate, endDate, warehouse }) {
  const { darkMode } = useTheme();
  const hasRange = Boolean(startDate && endDate);
  const { data: raw, loading, error } = useApiData(
    hasRange ? `/category_summary.php?od=${startDate}&do=${endDate}&warehouse=${warehouse || ""}` : null
  );
  const data = unwrapArray(raw);

  return (
    <Card title="Prodaja po kategorijama" icon={IconOverview}>
      <CategoryChartBody data={data} loading={loading} error={error} darkMode={darkMode} />
    </Card>
  );
}

function CategoryChartBody({ data, loading, error, darkMode }) {
  const colors = getChartColors(darkMode);
  if (error && (!data || data.length === 0)) return <ErrorState />;
  if (loading && (!data || data.length === 0)) return <ChartSkeleton height={260} />;
  if (!data || data.length === 0) return <EmptyState title="Nema podataka za izabrani period" />;

  const values = data.map((d) => d.vrednost);
  const total = values.reduce((sum, v) => sum + v, 0);

  const legendLabels = data.map((d) => {
    return d.category_name ? d.category_name.charAt(0).toUpperCase() + d.category_name.slice(1) : "Bez kategorije";
  });

  const chartData = {
    labels: legendLabels,
    datasets: [
      {
        data: values,
        backgroundColor: CHART_PALETTE,
        hoverOffset: 12,
        borderWidth: 2,
        borderColor: colors.surface,
      },
    ],
  };

  const options = {
    maintainAspectRatio: false,
    layout: { padding: 8 },
    plugins: {
      legend: {
        display: true,
        position: "bottom",
        labels: {
          color: colors.text,
          font: { size: 12 },
          padding: 14,
          boxWidth: 12,
          boxHeight: 12,
          usePointStyle: true,
        },
      },
      datalabels: {
        color: "#fff",
        // Only the percentage on the slice itself; name/icon are now in the legend, less clutter.
        formatter: (value) => {
          const pct = total ? ((value / total) * 100).toFixed(0) : "0";
          return pct >= 6 ? `${pct}%` : ""; // tiny slices get no text, color only
        },
        font: { weight: "bold", size: 12 },
      },
      tooltip: {
        ...tooltipStyle(colors),
        callbacks: {
          title: (items) => legendLabels[items[0].dataIndex],
          label: (context) => {
            const catData = data[context.dataIndex];
            const pct = total ? ((catData.vrednost / total) * 100).toFixed(1) : "0.0";
            const mainLine = `${formatNumber(catData.vrednost)} RSD (${pct}%)`;
            const topProds = (catData.top_products || []).map((p) => `${p.name}: ${formatNumber(p.vrednost)} RSD`);
            return [mainLine, ...topProds];
          },
        },
      },
    },
  };

  return (
    <div className="relative w-full min-w-0 overflow-hidden" style={{ maxWidth: 380, height: 360, margin: "0 auto" }}>
      <Pie data={chartData} options={options} />
    </div>
  );
}
